/**
 * Bulk Asset Onboarding Service
 *
 * Handles ZIP-based round-trip export/import of menu data + images.
 *
 * EXPORT: Generates a ZIP containing:
 *   - menu_data.xlsx  (structured Excel with Image_Filename column)
 *   - images/<Category>/<ItemName>.<ext>  (fetched from R2)
 *
 * IMPORT: Extracts a ZIP and returns:
 *   - ArrayBuffer of the Excel file inside for parsing
 *   - Map of image filenames → File objects for R2 upload
 */

import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import type { MenuProfile, MenuCategory, MenuItem } from '../types';
import { getMediaUrl } from '../../../core/fileUpload';

// ─── Helpers ────────────────────────────────────────────────────────────────

const sanitize = (name: string) =>
  name.replace(/[\/\\:*?"<>|]/g, '').trim().replace(/\s+/g, ' ');

function detectExt(blob: Blob): string {
  if (blob.type.includes('png')) return 'png';
  if (blob.type.includes('webp')) return 'webp';
  return 'jpg';
}

async function fetchImageBlob(url: string): Promise<{ data: ArrayBuffer; ext: string } | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return { data: await blob.arrayBuffer(), ext: detectExt(blob) };
  } catch {
    return null;
  }
}

// ─── Export: Build Complete Package ZIP ──────────────────────────────────────

export interface BulkExportOptions {
  profile: MenuProfile;
  /** Pre-generated Excel workbook as ArrayBuffer */
  excelBuffer: ArrayBuffer;
  excelFilename?: string;
  onProgress?: (percent: number) => void;
}

/**
 * Builds a ZIP containing the Excel file + all item images organized by
 * category folder. The Excel is generated externally so we don't duplicate
 * the template logic — we just bundle it.
 *
 * Image filenames in the ZIP match the `Image_Filename` column written by
 * the export engine so imports can round-trip.
 */
export async function exportBulkPackage({
  profile,
  excelBuffer,
  excelFilename = 'menu_data.xlsx',
  onProgress,
}: BulkExportOptions): Promise<void> {
  const zip = new JSZip();

  // 1. Add the Excel file at root
  zip.file(excelFilename, excelBuffer);

  // 2. Collect all images to fetch
  const imagesFolder = zip.folder('images')!;
  const tasks: { item: MenuItem; catName: string }[] = [];

  for (const cat of profile.categories) {
    const catName = sanitize(cat.nameEN || cat.nameAR || 'Untitled');
    for (const item of cat.items) {
      if (item.image) tasks.push({ item, catName });
    }
  }

  let completed = 0;
  const total = tasks.length;
  const report = (n: number) => onProgress?.(total > 0 ? Math.round((n / total) * 100) : 100);

  // 3. Fetch & add images (parallel, batched)
  const BATCH = 6;
  const catFolders = new Map<string, JSZip>();

  for (let i = 0; i < tasks.length; i += BATCH) {
    const batch = tasks.slice(i, i + BATCH);
    await Promise.all(
      batch.map(async ({ item, catName }) => {
        if (!catFolders.has(catName)) catFolders.set(catName, imagesFolder.folder(catName)!);
        const folder = catFolders.get(catName)!;
        const url = getMediaUrl(item.image);
        if (!url) { completed++; report(completed); return; }
        const img = await fetchImageBlob(url);
        if (img) {
          const name = sanitize(item.nameEN || item.nameAR || 'Item');
          folder.file(`${name}.${img.ext}`, img.data);
        }
        completed++;
        report(completed);
      }),
    );
  }

  // 4. Generate & save
  const blob = await zip.generateAsync({ type: 'blob' });
  const safeBrand = sanitize(profile.brandName || 'Brand');
  const safeMenu = sanitize(profile.name || 'Menu');
  saveAs(blob, `${safeBrand}_${safeMenu}_Bulk_Package.zip`);
}

// ─── Import: Extract ZIP → Excel + Image Map ───────────────────────────────

export interface BulkImportResult {
  /** The Excel ArrayBuffer extracted from the ZIP */
  excelBuffer: ArrayBuffer;
  /** Original filename of the detected Excel file */
  excelFilename: string;
  /**
   * Map of lowercase image filename (relative to images/ folder) → File object.
   * Key format: "category/itemname.jpg" (lowercased, from ZIP path).
   */
  imageFiles: Map<string, File>;
  /** Total number of image files found in the ZIP */
  imageCount: number;
}

/**
 * Extracts a bulk package ZIP and returns the Excel buffer + image map.
 * The caller is responsible for:
 *  1. Parsing the Excel via importMenuFromExcel()
 *  2. Uploading images to R2 based on the Image_Filename column mapping
 */
export async function extractBulkPackage(zipFile: File): Promise<BulkImportResult> {
  const zipData = await zipFile.arrayBuffer();
  const zip = await JSZip.loadAsync(zipData);

  // 1. Find the Excel file (root level, .xlsx or .xls)
  let excelEntry: JSZip.JSZipObject | null = null;
  let excelFilename = '';

  for (const [path, entry] of Object.entries(zip.files)) {
    if (entry.dir) continue;
    const name = path.toLowerCase();
    // Only root-level Excel files (no subfolder)
    if (!name.includes('/') && (name.endsWith('.xlsx') || name.endsWith('.xls'))) {
      excelEntry = entry;
      excelFilename = path;
      break;
    }
  }

  if (!excelEntry) {
    throw new Error('No Excel file (.xlsx) found at the root of the ZIP package.');
  }

  const excelBuffer = await excelEntry.async('arraybuffer');

  // 2. Collect image files from images/ folder
  const imageFiles = new Map<string, File>();
  const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif']);

  for (const [path, entry] of Object.entries(zip.files)) {
    if (entry.dir) continue;
    const lower = path.toLowerCase();
    if (!lower.startsWith('images/')) continue;
    const ext = lower.split('.').pop() || '';
    if (!IMAGE_EXTS.has(ext)) continue;

    const blob = await entry.async('blob');
    const mimeMap: Record<string, string> = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif' };
    const file = new File([blob], path.split('/').pop() || 'image', { type: mimeMap[ext] || 'image/jpeg' });

    // Key: relative path under images/, lowercased for matching
    // e.g. "burgers/classic burger.jpg"
    const relPath = path.substring('images/'.length).toLowerCase();
    imageFiles.set(relPath, file);
  }

  return {
    excelBuffer,
    excelFilename,
    imageFiles,
    imageCount: imageFiles.size,
  };
}

// ─── Image Matching: Link Excel rows to ZIP images ──────────────────────────

/**
 * Builds a map of item ID → File by matching the Image_Filename column
 * values from parsed categories against the extracted ZIP image files.
 *
 * Returns matches + a list of warnings for missing images.
 */
export function matchImagesToItems(
  categories: MenuCategory[],
  imageFiles: Map<string, File>,
  imageFilenames: Map<string, string>,
): { matches: Map<string, File>; warnings: string[] } {
  const matches = new Map<string, File>();
  const warnings: string[] = [];

  for (const cat of categories) {
    for (const item of cat.items) {
      // Check if we have a filename mapping for this item
      const filename = imageFilenames.get(item.id);
      if (!filename) continue;

      // Normalize the filename for lookup
      const key = filename.toLowerCase();
      const file = imageFiles.get(key);

      if (file) {
        matches.set(item.id, file);
      } else {
        warnings.push(`Image not found in ZIP for "${item.nameEN || item.nameAR}": ${filename}`);
      }
    }
  }

  return { matches, warnings };
}
